import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../rules/rules.css";
import "./spaceGameSettings.css";
import { selectAdminToken } from "../../sessionSlice";
import { getSpaceGameQuestions } from "../../functions/getSpaceGameQuestions";
import { deleteSpaceGameQuestion } from "../../functions/deleteSpaceGameQuestion";
import SpaceGameQuestionModal from "./SpaceGameQuestionModal";
import SpaceGameCsvUploadModal from "./SpaceGameCsvUploadModal";

const MAX_QUESTIONS = 30;

function secondsToMmSs(totalSeconds) {
  const n = Math.max(0, Number(totalSeconds) || 0);
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const SpaceGameSettings = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const loadQuestions = useCallback(async () => {
    if (!adminToken) return;
    try {
      const data = await getSpaceGameQuestions({ token: adminToken });
      setQuestions(data.questions || []);
    } catch (e) {
      console.error("Failed to load questions:", e);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAddClick = () => {
    setEditingQuestion(null);
    setModalOpen(true);
  };

  const handleEditClick = (q) => {
    setEditingQuestion(q);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditingQuestion(null);
    loadQuestions();
  };

  const handleCsvUploaded = () => {
    setCsvModalOpen(false);
    loadQuestions();
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete question?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    try {
      await deleteSpaceGameQuestion({ id, token: adminToken });
      loadQuestions();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to delete question.", "error");
    }
  };

  return (
    <div className="rules-page-layout">
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/lobby")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="admin-tab-container sgq-list-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-pen"></i> &nbsp; Game Settings &mdash; Questions ({questions.length}/{MAX_QUESTIONS})
            </div>

            {loading ? (
              <p className="sgq-empty">Loading...</p>
            ) : questions.length === 0 ? (
              <p className="sgq-empty">No questions added yet.</p>
            ) : (
              <div className="sgq-table-wrap">
                <table className="sgq-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Correct</th>
                      <th>Timer</th>
                      <th>Points</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q, idx) => (
                      <tr key={q.id}>
                        <td>{idx + 1}</td>
                        <td>{q.question_text}</td>
                        <td>{q.correct_answer}</td>
                        <td>{secondsToMmSs(q.timer_seconds)}</td>
                        <td>{q.points}</td>
                        <td className="sgq-row-actions">
                          <button className="sgq-icon-btn" onClick={() => handleEditClick(q)} title="Edit">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button className="sgq-icon-btn" onClick={() => handleDelete(q.id)} title="Delete">
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rules-action-holder">
              <button
                className="save-and-continue"
                onClick={handleAddClick}
                disabled={questions.length >= MAX_QUESTIONS}
              >
                + Add Question
              </button>
              <button className="sgq-upload-csv-btn" onClick={() => setCsvModalOpen(true)}>
                Upload CSV
              </button>
            </div>
            {questions.length >= MAX_QUESTIONS && (
              <p className="sgq-limit-note">Maximum of {MAX_QUESTIONS} questions reached.</p>
            )}
          </div>
        </div>
      </div>

      <SpaceGameQuestionModal
        isOpen={modalOpen}
        question={editingQuestion}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      <SpaceGameCsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        onUploaded={handleCsvUploaded}
      />
    </div>
  );
};

export default SpaceGameSettings;
